import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Calendar, Euro, RefreshCw, History, LineChart } from "lucide-react";
import { API, authHeaders, SOURCE_LABELS as SRC } from "./shared";
import SnapshotHistoryModal from "./SnapshotHistoryModal";

/**
 * Widget "Fatturato Stoccaggio Mese in Corso" + backfill controls.
 * Shown as a dedicated tab in AdminWarehouse. Fetches the same billing
 * endpoint used by the invoice PDF so admin can preview totals per
 * warehouse client before generating a fattura.
 */
export default function StorageBillingTab() {
  const currentMonth = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [snapping, setSnapping] = useState(false);
  const [historyClient, setHistoryClient] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/warehouse/storage-billing?month=${month}`, authHeaders());
      setData(r.data);
    } catch {
      toast.error("Errore nel caricamento del fatturato");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const snapshotNow = async () => {
    setSnapping(true);
    try {
      const r = await axios.post(`${API}/admin/warehouse/storage-snapshot`, {}, authHeaders());
      toast.success(`Snapshot creato per ${r.data.clients_snapshotted} clienti`);
      load();
    } catch {
      toast.error("Errore nello snapshot");
    } finally {
      setSnapping(false);
    }
  };

  const backfill = async () => {
    if (!window.confirm(`Ricostruire gli snapshot giornalieri di ${month} dai movimenti?\nSovrascrive eventuali snapshot già presenti in quel mese.`)) return;
    setBackfilling(true);
    try {
      const r = await axios.post(`${API}/admin/warehouse/storage-backfill`, { month }, authHeaders());
      toast.success(`Backfill completato: ${r.data.total_days_written} righe scritte`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore backfill");
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="storage-billing-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-white border border-zinc-200 rounded-sm px-3 py-2">
          <Calendar size={14} className="text-zinc-500" />
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="text-sm outline-none"
            data-testid="storage-billing-month"
          />
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 border border-zinc-300 bg-white px-3 py-2 text-xs font-bold uppercase tracking-wide rounded-sm hover:bg-zinc-50 disabled:opacity-50"
          data-testid="storage-billing-refresh"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Aggiorna
        </button>
        <button
          onClick={snapshotNow}
          disabled={snapping}
          title="Registra la giacenza corrente come snapshot di oggi"
          className="inline-flex items-center gap-2 border border-zinc-300 bg-white px-3 py-2 text-xs font-bold uppercase tracking-wide rounded-sm hover:bg-zinc-50 disabled:opacity-50"
          data-testid="storage-billing-snap"
        >
          {snapping ? <Loader2 size={12} className="animate-spin" /> : <Calendar size={12} />}
          Snapshot Oggi
        </button>
        <button
          onClick={backfill}
          disabled={backfilling}
          title="Ricostruisce gli snapshot giornalieri del mese selezionato a partire dai movimenti"
          className="inline-flex items-center gap-2 bg-blue-700 text-white px-3 py-2 text-xs font-bold uppercase tracking-wide rounded-sm hover:bg-blue-800 disabled:opacity-50"
          data-testid="storage-billing-backfill"
        >
          {backfilling ? <Loader2 size={12} className="animate-spin" /> : <History size={12} />}
          Backfill Mese
        </button>
        {data && (
          <div className="ml-auto flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 rounded-sm">
            <Euro size={14} />
            <span className="text-xs font-bold uppercase tracking-wide">Totale mese</span>
            <span className="text-lg font-bold" data-testid="storage-billing-grand-total">
              {(data.grand_total || 0).toFixed(2)} EUR
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center text-sm text-zinc-500">
          <Loader2 size={18} className="animate-spin inline mr-2" /> Calcolo in corso...
        </div>
      ) : !data || !data.clients?.length ? (
        <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center text-sm text-zinc-500" data-testid="storage-billing-empty">
          Nessun cliente con contratto stoccaggio abilitato per il mese selezionato.
          <br />
          <span className="text-xs">Abilita il contratto dalla pagina Clienti espandendo la scheda del cliente.</span>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Cliente</th>
                <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500" title="Media giornaliera bancali · fonte">Media bancali</th>
                <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Ingressi</th>
                <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Uscite</th>
                <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Stoccaggio</th>
                <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Handling</th>
                <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Assic.</th>
                <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500" title="Ore lavorate manuali del mese">Ore</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-widest text-zinc-800">Totale</th>
                <th className="text-center px-3 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Storico</th>
              </tr>
            </thead>
            <tbody>
              {data.clients.map((c) => (
                <tr key={c.client_email} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`storage-row-${c.client_email}`}>
                  <td className="px-4 py-3 text-sm">
                    <div className="font-bold text-[#09090B]">{c.client_name}</div>
                    <div className="text-[10px] text-zinc-400">{c.client_email}</div>
                  </td>
                  <td className="px-3 py-3 text-right text-sm">
                    {c.avg_pallets.toFixed(2)}
                    <div className="text-[9px] text-zinc-400 uppercase">{c.source}</div>
                  </td>
                  <td className="px-3 py-3 text-right text-sm">{c.in_pallets}</td>
                  <td className="px-3 py-3 text-right text-sm">{c.out_pallets}</td>
                  <td className="px-3 py-3 text-right text-sm">
                    {c.lines.storage.toFixed(2)}
                    <div className="text-[9px] text-zinc-400 uppercase" data-testid={`storage-src-${c.client_email}`}>
                      {c.tariff.storage_cost_per_pallet.toFixed(2)} €/plt/mese · {SRC[c.tariff_sources?.storage_cost_per_pallet] || "-"}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right text-sm">
                    {(c.lines.ingressi + c.lines.uscite).toFixed(2)}
                    <div className="text-[9px] text-zinc-400 uppercase">
                      in {c.tariff.in_cost_per_pallet.toFixed(2)} ({SRC[c.tariff_sources?.in_cost_per_pallet] || "-"}) · out {c.tariff.out_cost_per_pallet.toFixed(2)} ({SRC[c.tariff_sources?.out_cost_per_pallet] || "-"})
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right text-sm">
                    {c.lines.insurance.toFixed(2)}
                    {c.lines.insurance > 0 && (
                      <div className="text-[9px] text-zinc-400 uppercase">{SRC[c.tariff_sources?.monthly_insurance_flat] || "-"}</div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right text-sm">
                    <div>{(c.lines.hours || 0).toFixed(2)}</div>
                    {c.hours?.total_hours > 0 && (
                      <div className="text-[9px] text-zinc-400 uppercase">{c.hours.total_hours.toFixed(2)}h</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-bold text-[#FF5200]" data-testid={`storage-total-${c.client_email}`}>
                    {c.total.toFixed(2)} EUR
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button
                      onClick={() => setHistoryClient(c)}
                      title="Vedi storico giornaliero degli snapshot"
                      className="inline-flex items-center gap-1 border border-zinc-300 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-wide rounded-sm hover:bg-zinc-100"
                      data-testid={`storage-history-${c.client_email}`}
                    >
                      <LineChart size={11} /> Vedi
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {historyClient && (
        <SnapshotHistoryModal
          client={historyClient}
          defaultMonth={month}
          onClose={() => setHistoryClient(null)}
        />
      )}
    </div>
  );
}
